<script>
import { mapGetters } from 'vuex';
import MentionBox from '../mentions/MentionBox.vue';
import { parseLinks } from 'dashboard/helper/linkParser';

const LANGUAGE_NAMES = {
  en: 'English',
  fr: 'French',
  it: 'Italian',
  es: 'Spanish',
  nl: 'Dutch',
  fi: 'Finnish',
  da: 'Danish',
  sv: 'Swedish',
  no: 'Norwegian',
};

export default {
  components: { MentionBox },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
  },
  emits: ['replace'],
  data() {
    return {
      mode: 'templates', // 'templates' or 'languages'
      selectedTemplate: null,
      preferredLocale: null,
      manuallySelected: false,
    };
  },
  computed: {
    ...mapGetters({
      cannedMessages: 'getCannedResponses',
      detectedCannedLanguage: 'getDetectedLanguage',
    }),
    detectedLocale() {
      if (!this.detectedCannedLanguage) {
        return null;
      }
      return this.normalizeLocale(this.detectedCannedLanguage);
    },
    items() {
      if (this.mode === 'languages' && this.selectedTemplate) {
        return this.getLanguageItems();
      }
      return this.getTemplateItems();
    },
  },
  watch: {
    searchKey() {
      this.fetchCannedResponses();
      this.resetToTemplates();
    },
    detectedLocale: {
      immediate: true,
      handler(locale) {
        console.info('[CannedResponse] detectedLocale watcher fired', {
          locale,
          previousPreferred: this.preferredLocale,
          manuallySelected: this.manuallySelected,
          cannedCount: this.cannedMessages.length,
        });
        this.preferredLocale = locale;
        this.manuallySelected = false;
        this.ensureDetectedLanguageActive();
      },
    },
    cannedMessages: {
      handler() {
        this.ensureDetectedLanguageActive();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchCannedResponses();
  },
  methods: {
    fetchCannedResponses() {
      this.$store.dispatch('getCannedResponse', { searchKey: this.searchKey });
    },
    ensureDetectedLanguageActive() {
      console.info('[CannedResponse] ensureDetectedLanguageActive invoked', {
        preferredLocale: this.preferredLocale,
        manuallySelected: this.manuallySelected,
        cannedCount: this.cannedMessages.length,
      });
      if (this.manuallySelected) {
        console.info('[CannedResponse] skipping auto selection due to manual selection');
        return;
      }

      const locale = this.preferredLocale;
      if (!locale || !this.cannedMessages.length) {
        console.info('[CannedResponse] no locale or canned messages to process', {
          locale,
          cannedCount: this.cannedMessages.length,
        });
        return;
      }

      const matchingTemplate = this.cannedMessages.find(template =>
        this.templateHasLocale(template, locale)
      );

      if (!matchingTemplate) {
        console.info('[CannedResponse] no matching template for locale', {
          locale,
        });
        return;
      }

      console.info('[CannedResponse] matching template found', {
        locale,
        templateId: matchingTemplate.id,
        shortCode: matchingTemplate.short_code,
        mode: this.mode,
      });

      if (this.mode === 'languages') {
        if (!this.selectedTemplate || this.selectedTemplate.id !== matchingTemplate.id) {
          console.info('[CannedResponse] updating selected template for language view', {
            locale,
            templateId: matchingTemplate.id,
          });
          this.selectedTemplate = matchingTemplate;
        }
        return;
      }

      if (this.selectedTemplate) {
        console.info('[CannedResponse] clearing auto-selected template to stay in templates mode');
        this.selectedTemplate = null;
      }

      console.info('[CannedResponse] staying in templates mode; detected template will be highlighted', {
        locale,
        templateId: matchingTemplate.id,
      });
    },
    templateHasLocale(template, locale) {
      if (!locale) {
        return false;
      }
      const normalized = this.normalizeLocale(locale);
      const templateLocale = this.normalizeLocale(template.locale);

      if (templateLocale === normalized) {
        return true;
      }

      if (!template?.translations) {
        return false;
      }

      return template.translations.some(translation =>
        this.normalizeLocale(translation.locale) === normalized
      );
    },

    buildTemplateItem(cannedMessage) {
      const locale = this.normalizeLocale(cannedMessage.locale);
      const isDetected = this.templateHasLocale(cannedMessage, this.preferredLocale);
      return {
        label: cannedMessage.short_code,
        key: cannedMessage.short_code,
        description: cannedMessage.content,
        hasTranslations:
          Array.isArray(cannedMessage.translations) &&
          cannedMessage.translations.length > 0,
        template: cannedMessage,
        locale,
        language: this.getLanguageName(locale),
        isDetected,
      };
    },

    getTemplateItems() {
      const items = this.cannedMessages.map(cannedMessage =>
        this.buildTemplateItem(cannedMessage)
      );

      if (!this.preferredLocale) {
        return items;
      }

      const detectedItems = items.filter(item => item.isDetected);
      const otherItems = items.filter(item => !item.isDetected);

      return [...detectedItems, ...otherItems];
    },

    getLanguageItems() {
      const items = [];

      items.push({
        label: '← Back to templates',
        key: 'back',
        isBackButton: true,
      });

      if (!this.selectedTemplate) {
        return items;
      }

      const languageEntries = [];

      languageEntries.push({
        label: `🇬🇧 ${this.selectedTemplate.short_code} (English)`,
        key: 'en',
        description: this.selectedTemplate.content,
        locale: this.normalizeLocale('en'),
        language: 'English',
        template: this.selectedTemplate,
        isDetected: this.normalizeLocale('en') === this.preferredLocale,
      });

      if (this.selectedTemplate.translations) {
        this.selectedTemplate.translations.forEach(translation => {
          languageEntries.push({
            label: `${this.getLanguageFlag(translation.locale)} ${this.selectedTemplate.short_code} (${this.getLanguageName(translation.locale)})`,
            key: translation.locale,
            description: translation.content,
            locale: this.normalizeLocale(translation.locale),
            language: this.getLanguageName(translation.locale),
            template: this.selectedTemplate,
            isDetected:
              this.normalizeLocale(translation.locale) === this.preferredLocale,
          });
        });
      }

      const detectedEntry = languageEntries.find(entry => entry.isDetected);
      console.info('[CannedResponse] built language entries', {
        preferredLocale: this.preferredLocale,
        totalEntries: languageEntries.length,
        detectedKey: detectedEntry?.key,
        detectedLocale: detectedEntry?.locale,
      });

      const prioritized = languageEntries.sort((a, b) => {
        if (a.isDetected === b.isDetected) {
          return 0;
        }
        return a.isDetected ? -1 : 1;
      });

      return [...items, ...prioritized];
    },
    
    getLanguageFlag(locale) {
      const flags = {
        'fr': '🇫🇷',
        'it': '🇮🇹',
        'es': '🇪🇸',
        'nl': '🇳🇱',
        'fi': '🇫🇮',
        'da': '🇩🇰',
        'sv': '🇸🇪',
        'no': '🇳🇴',
      };
      return flags[locale] || '🌐';
    },

    getLanguageName(locale) {
      const normalized = this.normalizeLocale(locale);
      return LANGUAGE_NAMES[normalized] || normalized.toUpperCase();
    },

    normalizeLocale(locale) {
      if (locale === null || locale === undefined) {
        return 'en';
      }

      const stringLocale = String(locale).trim();

      if (!stringLocale) {
        return 'en';
      }

      const normalized = stringLocale.toLowerCase().replace(/_/g, '-');
      const [base] = normalized.split('-');

      return base || 'en';
    },

    handleMentionClick(item = {}) {
      // Handle back button
      if (item.isBackButton) {
        this.resetToTemplates();
        return;
      }

      // If in templates mode and has translations, show languages
      if (this.mode === 'templates' && item.hasTranslations) {
        this.selectedTemplate = item.template;
        this.mode = 'languages';
        return;
      }

      // Parse links in content before inserting
      const contentWithLinks = parseLinks(item.description);

      const template = item.template || this.selectedTemplate || item;
      const locale = this.normalizeLocale(item.locale || template?.locale);
      const language = item.language || this.getLanguageName(locale);
      const shortCode = template?.short_code || '';
      const templateName = template?.title || template?.name || template?.short_code || '';

      this.manuallySelected = true;
      this.preferredLocale = locale;

      console.info('[CannedResponse] handleMentionClick emit replace', {
        locale,
        language,
        shortCode,
        templateId: template?.id,
      });

      this.$emit('replace', {
        content: contentWithLinks,
        cannedReply: {
          id: template?.id,
          shortCode,
          locale,
          language,
          name: templateName,
        },
      });
      this.resetToTemplates();
    },
    
    resetToTemplates() {
      this.mode = 'templates';
      this.selectedTemplate = null;
      this.manuallySelected = false;
      console.info('[CannedResponse] resetToTemplates');
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <MentionBox
    v-if="items.length"
    :items="items"
    :detected-locale="preferredLocale"
    @mention-select="handleMentionClick"
  />
</template>
